// ---------------------------------------------------------------------------
// narrationSettings — device-local preferences for the AI scenic narration
// feature. Stored in localStorage so they survive reloads without needing
// to round-trip to the server. No PII; no cross-device sync needed.
//
// Shape:
//   pleasant.narrationIntensity = "quiet" | "balanced" | "generous"
//   pleasant.narrationTopics    = JSON: { history: bool, art: bool, ... }
//
// See docs/NARRATION_VOICE.md for the rules these settings drive.
// ---------------------------------------------------------------------------

// "off" silences scenic narration entirely — for a drive you do all the time
// and don't want talked through. The turn-by-turn voice is unaffected.
export type NarrationIntensity = "off" | "quiet" | "balanced" | "generous";

export const NARRATION_TOPICS = [
  "history",
  "art",
  "food",
  "nature",
  "water",
  "people",
  "road",
] as const;

export type NarrationTopic = (typeof NARRATION_TOPICS)[number];

export type NarrationTopicPrefs = Record<NarrationTopic, boolean>;

const INTENSITY_KEY = "pleasant.narrationIntensity";
const TOPICS_KEY = "pleasant.narrationTopics";

const DEFAULT_TOPICS: NarrationTopicPrefs = {
  history: true,
  art: true,
  food: true,
  nature: true,
  water: true,
  people: true,
  road: true,
};

export function getNarrationIntensity(): NarrationIntensity {
  if (typeof window === "undefined") return "balanced";
  try {
    const v = window.localStorage.getItem(INTENSITY_KEY);
    if (v === "off" || v === "quiet" || v === "balanced" || v === "generous") return v;
  } catch { /* ignore */ }
  return "balanced";
}

export function setNarrationIntensity(v: NarrationIntensity): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(INTENSITY_KEY, v); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("pleasant:narration-prefs-changed"));
}

export function getNarrationTopics(): NarrationTopicPrefs {
  if (typeof window === "undefined") return DEFAULT_TOPICS;
  try {
    const raw = window.localStorage.getItem(TOPICS_KEY);
    if (!raw) return DEFAULT_TOPICS;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out = { ...DEFAULT_TOPICS };
      for (const k of NARRATION_TOPICS) {
        if (typeof parsed[k] === "boolean") out[k] = parsed[k];
      }
      return out;
    }
  } catch { /* ignore */ }
  return DEFAULT_TOPICS;
}

export function setNarrationTopics(prefs: Partial<NarrationTopicPrefs>): void {
  if (typeof window === "undefined") return;
  const merged = { ...getNarrationTopics(), ...prefs };
  try { window.localStorage.setItem(TOPICS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("pleasant:narration-prefs-changed"));
}

/** Returns true if at least one tag on the narration matches a topic the
 *  user has enabled. An empty tag list is treated as "always allowed"
 *  (so untagged content slips through rather than being silently dropped). */
export function topicsAllow(tags: readonly string[], prefs: NarrationTopicPrefs): boolean {
  if (!tags.length) return true;
  return tags.some((t) => (prefs as Record<string, boolean | undefined>)[t] === true);
}

/** Cooldown in seconds between narrations based on intensity. */
export function cooldownSecondsFor(intensity: NarrationIntensity): number {
  switch (intensity) {
    case "off":       return Number.POSITIVE_INFINITY;
    case "quiet":     return Number.POSITIVE_INFINITY; // one narration per drive
    case "balanced":  return 240;  // 4 min
    case "generous":  return 90;
  }
}

/** Confidence threshold (0..1) for whether a feature is worth narrating. */
export function confidenceThresholdFor(intensity: NarrationIntensity): number {
  switch (intensity) {
    case "off":      return 1.1;   // unreachable — narration is disabled
    case "quiet":    return 0.9;
    case "balanced": return 0.7;
    case "generous": return 0.5;
  }
}

// ---------------------------------------------------------------------------
// Narration diagnostics — records the outcome of the most recent narration
// decision so the Settings screen can show "last drive: spoke" / "edge error"
// without the user needing a developer console. Pure local; no PII.
// ---------------------------------------------------------------------------

const LAST_OUTCOME_KEY = "pleasant.narrationLastOutcome";

export interface NarrationOutcome {
  /** What happened: "spoke" | "edge-failed" | "no-text" | "gated:<why>". */
  reason: string;
  /** Feature name involved, when there was one. */
  feature?: string;
  /** ms since epoch. */
  at: number;
}

export function recordNarrationOutcome(reason: string, feature?: string): void {
  if (typeof window === "undefined") return;
  try {
    const rec: NarrationOutcome = { reason, feature, at: Date.now() };
    window.localStorage.setItem(LAST_OUTCOME_KEY, JSON.stringify(rec));
  } catch { /* ignore quota */ }
}

export function getLastNarrationOutcome(): NarrationOutcome | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LAST_OUTCOME_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p.reason === "string" && typeof p.at === "number") {
      return p as NarrationOutcome;
    }
  } catch { /* ignore */ }
  return null;
}
